import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import SearchableSelect from "@/components/SearchableSelect";
import api, { formatDetail } from "@/lib/api";
import { fmtDate, formatIDR } from "@/lib/constants";

const TITLES = { verify: "Verifikasi Realisasi", reject: "Tolak Realisasi", cancel: "Batalkan Realisasi", link: "Tautkan Sumber Saldo", correct: "Koreksi Realisasi", history: "Riwayat Perubahan" };

function useFunding(needItems) {
  const [holders, setHolders] = useState([]);
  const [holderId, setHolderId] = useState("");
  const [subs, setSubs] = useState([]);
  const [pcId, setPcId] = useState("");
  useEffect(() => { api.get("/pc-manual-realizations/funding-options").then(({ data }) => setHolders(data.holders || [])).catch(() => {}); }, []);
  useEffect(() => {
    setPcId(""); setSubs([]);
    if (holderId) api.get("/pc-manual-realizations/funding-options", { params: { holder_id: holderId, include_items: needItems } }).then(({ data }) => setSubs(data.submissions || [])).catch(() => {});
  }, [holderId, needItems]);
  return { holders, holderId, setHolderId, subs, pcId, setPcId };
}

function FundingPicker({ f }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <SearchableSelect testid="pcm-act-holder" value={f.holderId} onChange={f.setHolderId} options={f.holders.map((h) => ({ value: h.id, label: h.name }))} placeholder="Pemegang petty cash…" searchPlaceholder="Ketik nama…" emptyText="Tidak ada" />
      <SearchableSelect testid="pcm-act-pc" value={f.pcId} onChange={f.setPcId} options={f.subs.map((s) => ({ value: s.id, label: `${s.week_label || "-"} · ${s.project_name || "-"}` }))} placeholder="Pengajuan ditransfer…" searchPlaceholder="Ketik…" emptyText="Tidak ada" />
    </div>
  );
}

function History({ rid }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/pc-manual-realizations/${rid}`).then(({ data }) => setD(data)).catch(() => setD({ audit: [], allocations: [] })); }, [rid]);
  if (!d) return <p className="text-xs italic text-slate-400">Memuat…</p>;
  return (
    <div className="space-y-2 text-xs max-h-[60vh] overflow-y-auto" data-testid="pcm-history">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">Alokasi Cost Control ({d.allocations.length})</p>
      {d.allocations.map((a) => <div key={a.id} className="border-l-2 border-sky-300 pl-2">{formatIDR(a.amount)} · {a.created_by_name} · {fmtDate(String(a.created_at).slice(0, 10))}</div>)}
      <p className="text-[10px] uppercase tracking-wider text-slate-500 pt-2">Audit</p>
      {d.audit.map((a) => (
        <div key={a.id} className="border-l-2 border-slate-300 pl-2">
          <b>{a.action}</b> · {a.actor_name} · {String(a.at).slice(0, 16).replace("T", " ")}{a.reason ? ` · ${a.reason}` : ""}
          {a.before && <div className="text-[10px] text-slate-500 break-all">Sebelum: {JSON.stringify(a.before)}</div>}
          {a.after && <div className="text-[10px] text-slate-500 break-all">Sesudah: {JSON.stringify(a.after)}</div>}
        </div>
      ))}
    </div>
  );
}

export default function PcmActionDialog({ act, onClose, onDone }) {
  const { kind, r } = act;
  const [reason, setReason] = useState("");
  const [fix, setFix] = useState({ amount: r?.amount ?? "", description: r?.description ?? "", transaction_date: r?.transaction_date ?? "" });
  const [busy, setBusy] = useState(false);
  const f = useFunding(false);
  const submit = async () => {
    setBusy(true);
    try {
      if (kind === "verify") await api.post(`/pc-manual-realizations/${r.id}/verify`);
      else if (kind === "reject" || kind === "cancel") await api.post(`/pc-manual-realizations/${r.id}/${kind}`, { reason });
      else if (kind === "link") await api.post(`/pc-manual-realizations/${r.id}/link-source`, { holder_id: f.holderId, pc_id: f.pcId });
      else if (kind === "correct") {
        const body = { reason };
        if (Number(fix.amount) !== Number(r.amount)) body.amount = Number(fix.amount);
        if (fix.description !== r.description) body.description = fix.description;
        if (fix.transaction_date !== r.transaction_date) body.transaction_date = fix.transaction_date;
        await api.patch(`/pc-manual-realizations/${r.id}`, body);
      }
      toast.success("Tersimpan");
      onDone();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Gagal menyimpan"); } finally { setBusy(false); }
  };
  const needReason = !["verify", "link", "history"].includes(kind);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl rounded-none" data-testid="pcm-action-dialog">
        <DialogHeader><DialogTitle className="text-sm uppercase tracking-wider">{TITLES[kind]}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-600 border border-slate-200 bg-slate-50 px-2 py-1.5">
          {`${fmtDate(r.transaction_date)} · ${r.description} · ${formatIDR(r.amount)}`}
        </div>
        {kind === "history" && <History rid={r.id} />}
        {kind === "verify" && <p className="text-xs text-slate-600">Setelah diverifikasi: mengurangi saldo pertanggungjawaban <b>{r.holder_name}</b> ({r.pc_week_label}) dan muncul sekali di Klasifikasi Pengeluaran Cost Control.</p>}
        {kind === "cancel" && r?.allocation_count > 0 && <p className="text-xs text-rose-700" data-testid="pcm-cancel-alloc-warn">{r.allocation_count} alokasi Cost Control ({formatIDR(r.allocated_total)}) akan ikut dibatalkan dan tercatat di riwayat.</p>}
        {kind === "link" && <FundingPicker f={f} />}
        {kind === "correct" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input type="date" value={fix.transaction_date || ""} onChange={(ev) => setFix({ ...fix, transaction_date: ev.target.value })} data-testid="pcm-fix-date" className="h-9 rounded-none" />
            <Input value={fix.amount} onChange={(ev) => setFix({ ...fix, amount: ev.target.value.replace(/[^0-9.]/g, "") })} data-testid="pcm-fix-amount" className="h-9 rounded-none text-right" />
            <Input value={fix.description} onChange={(ev) => setFix({ ...fix, description: ev.target.value })} data-testid="pcm-fix-desc" className="h-9 rounded-none" />
          </div>
        )}
        {needReason && <textarea rows={2} value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="Alasan (wajib)" data-testid="pcm-act-reason" className="w-full text-xs border border-slate-300 px-2 py-1.5" />}
        {kind !== "history" && (
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="h-9 px-3 border border-slate-300 text-xs uppercase tracking-wider font-semibold" data-testid="pcm-act-cancel">Tutup</button>
            <button type="button" disabled={busy || (needReason && !reason.trim())} onClick={submit} data-testid="pcm-act-submit"
              className="h-9 px-4 bg-[#0B1D3A] text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-50">{busy ? "Menyimpan…" : "Simpan"}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
